/**
 * SAP holiday-calendar + work-schedule — pure types + merge logic.
 *
 * IO (Supabase reads/writes, the live pull) lives in
 * services/sapCalendarService.ts. This module is side-effect free so the
 * consuming-side merge seam is unit-testable without a Supabase client — the
 * same lib/service split as sapChargeObjects.ts ↔ chargeObjectService.ts.
 */
import type { Holiday } from './holidays'

export type CalendarSource = 'sap_import' | 'manual'
export type SchedulePattern = '5x8' | '4x10' | '9x80' | 'custom'

export interface SapHoliday {
  orgId: string
  id: string
  calendarId: string
  date: string // ISO yyyy-MM-dd
  name: string | null
  holidayClass: string | null
  isWorkday: boolean
  active: boolean
  source: CalendarSource
}

export interface SapWorkSchedule {
  orgId: string
  id: string
  scheduleRule: string
  name: string | null
  weeklyHours: number | null
  pattern: string | null
  dailyTargets: Record<string, number> | null
  active: boolean
  source: CalendarSource
}

const VALID_PATTERNS: readonly SchedulePattern[] = ['5x8', '4x10', '9x80', 'custom']

/** Map a landed SAP schedule to a users.schedule_type pattern (falls back to custom). */
export function sapScheduleToPattern(s: Pick<SapWorkSchedule, 'pattern'>): SchedulePattern {
  return (VALID_PATTERNS as readonly string[]).includes(s.pattern ?? '')
    ? (s.pattern as SchedulePattern)
    : 'custom'
}

/** Map a landed SAP holiday to the app's Holiday shape (lib/holidays.ts). */
export function sapHolidayToHoliday(h: Pick<SapHoliday, 'date' | 'name'>): Holiday {
  return { date: h.date, name: h.name ?? 'Holiday' }
}

/**
 * Overlay SAP-landed holidays onto the built-in federal calendar. Additive +
 * deduped by date, with the SAP entry winning on a same-date conflict (a
 * customer's own calendar is authoritative). A SAP "working" special day removes
 * that date from the holiday set (it is not a day off). Returns a date-sorted
 * list. Pure — the seam the timesheet's standard-day logic can adopt later.
 */
export function mergeHolidaysWithFederal(
  federal: readonly Holiday[],
  sap: readonly SapHoliday[],
): Holiday[] {
  const byDate = new Map<string, Holiday>()
  for (const h of federal) byDate.set(h.date, h)
  for (const s of sap) {
    if (!s.active) continue
    if (s.isWorkday) { byDate.delete(s.date); continue } // SAP says this date is a working day
    byDate.set(s.date, sapHolidayToHoliday(s)) // SAP overrides / adds
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
}
